import { CodeTypeFormValues } from '@/components/account/interview/codes/validations';
import {
  ConversationCode,
  ConversationCodeWType,
  InterviewCodeType
} from '@/types/pulsedb';
import { CodeSourceTypeEnum, QueryRequest } from '@/types/pulse';
import { codetypeOptions, validateCodeValue } from '@/utils/pulse/codeValues';
import type { Json } from '@/types/db';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getConversationCodesForType = cache(
  async (
    supabase: SupabaseClient,
    codetypeId: number
  ): Promise<ConversationCode[] | null> => {
    const { data } = await supabase
      .from('conversation_codes')
      .select('*, conversations!inner(deleted)')
      .match({ codetype_id: codetypeId })
      .eq('conversations.deleted', false)
      .order('id', { ascending: false })
      .throwOnError();
    return data;
  }
);

/** The API caps a response, so a signal's values are read a page at a time. */
const CODE_VALUE_PAGE_SIZE = 1000;

/**
 * Every distinct value a signal holds, for the controls that offer them. Read
 * in full rather than in one capped request, so a value is offerable however
 * many conversations the interview has.
 */
export const getCodeValuesForType = cache(
  async (supabase: SupabaseClient, codetypeId: number): Promise<string[]> => {
    const values = new Set<string>();
    for (let page = 0; ; page++) {
      const from = page * CODE_VALUE_PAGE_SIZE;
      const { data } = await supabase
        .from('conversation_codes')
        .select('value, conversations!inner(deleted, hidden)')
        .eq('codetype_id', codetypeId)
        .eq('conversations.deleted', false)
        .eq('conversations.hidden', false)
        .order('id', { ascending: false })
        .range(from, from + CODE_VALUE_PAGE_SIZE - 1)
        .throwOnError();

      const rows = data ?? [];
      for (const row of rows) {
        if (row.value) values.add(row.value as string);
      }
      if (rows.length < CODE_VALUE_PAGE_SIZE) break;
    }
    return Array.from(values);
  }
);

export const getInterviewCodetypes = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    filters: Record<string, unknown> = {},
    sources?: number[]
  ): Promise<InterviewCodeType[] | null> => {
    let query = supabase
      .from('interview_codetypes')
      .select('*')
      .match({ interview_id: interviewId, ...filters })
      .eq('deleted', false)
      .eq('hidden', false);

    if (sources && sources.length > 0) {
      query = query.in('source', sources);
    }

    const { data } = await query
      .order('id', { ascending: false })
      .throwOnError();
    return data;
  }
);

export const deleteInterviewCodeType = async (
  supabase: SupabaseClient,
  id: number
) => {
  await supabase
    .from('interview_codetypes')
    .update({ deleted: true })
    .eq('id', id)
    .throwOnError();
};

export async function updateInterviewCodeType(
  supabase: SupabaseClient,
  id: number,
  data: Partial<CodeTypeFormValues>
) {
  const { data: updatedCode } = await supabase
    .from('interview_codetypes')
    .update(data)
    .eq('id', id)
    .select()
    .single()
    .throwOnError();

  return updatedCode;
}

export async function insertInterviewCodeType(
  supabase: SupabaseClient,
  interviewId: number,
  data: CodeTypeFormValues
) {
  const { data: newCode } = await supabase
    .from('interview_codetypes')
    .insert({
      ...data,
      interview_id: interviewId
    })
    .select()
    .single()
    .throwOnError();

  return newCode;
}

export const getConversationCodesForRespondent = cache(
  async (
    supabase: SupabaseClient,
    respondentId: number
  ): Promise<ConversationCodeWType[] | null> => {
    const { data } = await supabase
      .from('conversation_codes')
      .select('*, interview_codetypes(*), conversations!inner(respondent_id)')
      .eq('conversations.respondent_id', respondentId)
      .eq('interview_codetypes.deleted', false)
      .eq('interview_codetypes.hidden', false)
      .order('id', { ascending: false })
      .throwOnError();

    return data as unknown as ConversationCodeWType[] | null;
  }
);

export async function upsertTopicCodeType(
  supabase: SupabaseClient,
  interviewId: number,
  topicGuid: string,
  data: {
    name: string;
    description: string;
    datatype: number;
    multiple: boolean;
    source: number;
    query_request: any;
    auto_run: boolean;
  }
) {
  const { data: existing } = await supabase
    .from('interview_codetypes')
    .select('id')
    .match({ interview_id: interviewId, topic_guid: topicGuid })
    .maybeSingle();

  if (existing) {
    const { data: updated } = await supabase
      .from('interview_codetypes')
      .update(data)
      .eq('id', existing.id)
      .select()
      .single()
      .throwOnError();
    return updated;
  } else {
    const { data: inserted } = await supabase
      .from('interview_codetypes')
      .insert({
        ...data,
        interview_id: interviewId,
        topic_guid: topicGuid
      })
      .select()
      .single()
      .throwOnError();
    return inserted;
  }
}

export async function deleteTopicCodeTypes(
  supabase: SupabaseClient,
  interviewId: number,
  source: number,
  excludeGuids: string[]
) {
  let query = supabase
    .from('interview_codetypes')
    .update({ deleted: true })
    .eq('interview_id', interviewId)
    .eq('source', source)
    .not('topic_guid', 'is', null);

  if (excludeGuids.length > 0) {
    query = query.not('topic_guid', 'in', `(${excludeGuids.join(',')})`);
  }

  await query.throwOnError();
}

/**
 * Checks a value against the codetype it is being stored under and returns the
 * string to persist, throwing when the two disagree.
 *
 * Every write below goes through this, so a caller that never looks at the
 * datatype itself still cannot put a value in the table that contradicts it.
 * Callers writing many values at once should check them with `validateCodeValue`
 * first and drop the ones that fail, so one bad value does not end the run.
 */
async function validatedCodeValue(
  supabase: SupabaseClient,
  codetypeId: number,
  value: string
): Promise<string> {
  const { data: codetype } = await supabase
    .from('interview_codetypes')
    .select('datatype, query_request')
    .eq('id', codetypeId)
    .single()
    .throwOnError();

  const check = validateCodeValue(
    codetype.datatype,
    value,
    codetypeOptions(codetype)
  );
  if (!check.ok) throw new Error(check.error);
  return check.value;
}

export async function insertConversationCode(
  supabase: SupabaseClient,
  data: {
    conversation_id: number;
    codetype_id: number;
    value: string;
  }
): Promise<ConversationCode | null> {
  const value = await validatedCodeValue(
    supabase,
    data.codetype_id,
    data.value
  );

  const { data: newCode } = await supabase
    .from('conversation_codes')
    .insert({ ...data, value })
    .select()
    .single()
    .throwOnError();
  return newCode;
}

export async function updateConversationCode(
  supabase: SupabaseClient,
  id: number,
  data: { value: string }
): Promise<ConversationCode | null> {
  // The codetype comes from the row being edited: an update carries only the new
  // value, not what it is meant to be.
  const { data: existing } = await supabase
    .from('conversation_codes')
    .select('codetype_id')
    .eq('id', id)
    .single()
    .throwOnError();

  const value = await validatedCodeValue(
    supabase,
    existing.codetype_id,
    data.value
  );

  const { data: updatedCode } = await supabase
    .from('conversation_codes')
    .update({ value })
    .eq('id', id)
    .select()
    .single()
    .throwOnError();
  return updatedCode;
}

export async function deleteConversationCode(
  supabase: SupabaseClient,
  id: number
): Promise<void> {
  await supabase
    .from('conversation_codes')
    .delete()
    .eq('id', id)
    .throwOnError();
}

/**
 * Updates the existing code for (conversation, codetype) if present, inserts
 * otherwise.
 */
export async function upsertConversationCode(
  supabase: SupabaseClient,
  conversationId: number,
  codetypeId: number,
  fields: {
    value?: string | null;
    quote?: string | null;
    transcript_index?: number | null;
  }
): Promise<void> {
  // A caller may be touching only the quote or transcript index, so the value is
  // checked when one is actually being written.
  const checked =
    typeof fields.value === 'string'
      ? {
          ...fields,
          value: await validatedCodeValue(supabase, codetypeId, fields.value)
        }
      : fields;

  const { data: existing } = await supabase
    .from('conversation_codes')
    .select('id')
    .eq('conversation_id', conversationId)
    .eq('codetype_id', codetypeId)
    .limit(1)
    .maybeSingle()
    .throwOnError();

  if (!existing) {
    await supabase
      .from('conversation_codes')
      .insert({
        conversation_id: conversationId,
        codetype_id: codetypeId,
        ...checked
      })
      .throwOnError();
  } else {
    await supabase
      .from('conversation_codes')
      .update(checked)
      .eq('conversation_id', conversationId)
      .eq('codetype_id', codetypeId)
      .throwOnError();
  }
}

/**
 * Matches an existing codetype by name OR description (case-sensitive, truncated
 * to 250 chars) within the interview; updates it or inserts a new one. Returns
 * the codetype id.
 */
export async function upsertInterviewCode(
  supabase: SupabaseClient,
  interviewId: number,
  code: string,
  fields: {
    datatype?: number;
    multiple?: boolean;
    source?: number;
    description?: string;
    key?: string;
    internal?: boolean;
    auto_run?: boolean;
  }
): Promise<number> {
  const name = code.slice(0, 250).trim();

  // Match an existing codetype by name OR description using column-safe `.eq`
  // lookups instead of a hand-built `.or('name.eq."…"')` string. The quoted-value
  // `.or` form trips up postgrest-js on values containing spaces — it surfaces as
  // a bogus "column interview_codetypes.name does not exist".
  const existingId =
    (await findCodetypeId(supabase, interviewId, 'name', name)) ??
    (await findCodetypeId(supabase, interviewId, 'description', name));

  if (existingId == null) {
    const { data } = await supabase
      .from('interview_codetypes')
      .insert({ interview_id: interviewId, name, description: name, ...fields })
      .select('id')
      .single()
      .throwOnError();
    return data.id;
  }

  await supabase
    .from('interview_codetypes')
    .update(fields)
    .eq('id', existingId)
    .throwOnError();
  return existingId;
}

/**
 * Insert a report-generated codetype (source = REPORT, hidden so it stays out
 * of the general codes UI but is still referenced by the report via
 * `interview_codetype_ids`). Stores the QueryRequest verbatim and copies its
 * datatype onto the row.
 */
export async function createReportCodetype(
  supabase: SupabaseClient,
  args: {
    interviewId: number;
    name: string;
    description: string;
    queryRequest: QueryRequest;
  }
): Promise<InterviewCodeType> {
  const { data } = await supabase
    .from('interview_codetypes')
    .insert({
      interview_id: args.interviewId,
      name: args.name,
      description: args.description,
      query_request: args.queryRequest as unknown as Json,
      datatype: args.queryRequest.datatype,
      source: CodeSourceTypeEnum.REPORT,
      hidden: true
    })
    .select('*')
    .single()
    .throwOnError();
  return data as unknown as InterviewCodeType;
}

/** Set a codetype's `hidden` flag (used to retire a previous report's signals). */
export async function setInterviewCodetypeHidden(
  supabase: SupabaseClient,
  id: number,
  hidden: boolean
): Promise<void> {
  await supabase
    .from('interview_codetypes')
    .update({ hidden })
    .eq('id', id)
    .throwOnError();
}

/**
 * Inserts a codetype with an explicit source / query_request / auto_run — the
 * signal-creation path (MCP tools), which sets fields the UI form
 * (insertInterviewCodeType) doesn't expose.
 */
export async function createSignalCodetype(
  supabase: SupabaseClient,
  interviewId: number,
  fields: {
    name: string;
    description?: string | null;
    datatype: number;
    multiple: boolean;
    source: number;
    query_request: QueryRequest | null;
    auto_run: boolean;
  }
): Promise<InterviewCodeType> {
  const { data } = await supabase
    .from('interview_codetypes')
    .insert({
      interview_id: interviewId,
      ...fields,
      query_request: fields.query_request as any
    })
    .select('*')
    .single()
    .throwOnError();
  return data;
}

/**
 * The interview's first VISIBLE (non-deleted, non-hidden, non-internal)
 * codetype whose name matches case-insensitively, else null. Used for
 * duplicate detection on create — the visibility scope matches what the signal
 * read tools expose, so a hidden/internal codetype never blocks (or leaks
 * through) a signal name.
 *
 * The comparison runs in JS rather than ilike: PostgREST treats '*' in
 * like/ilike patterns as an alias for '%', so a pattern can never match a
 * literal '*' — and an interview's visible codetype set is small.
 */
export async function findVisibleCodetypeByName(
  supabase: SupabaseClient,
  interviewId: number,
  name: string
): Promise<InterviewCodeType | null> {
  const { data } = await supabase
    .from('interview_codetypes')
    .select('*')
    .eq('interview_id', interviewId)
    .eq('deleted', false)
    .eq('hidden', false)
    .eq('internal', false)
    .throwOnError();

  const target = name.trim().toLowerCase();
  return (
    (data ?? []).find(
      (ct: InterviewCodeType) => ct.name.toLowerCase() === target
    ) ?? null
  );
}

/**
 * Distinct-conversation counts per codetype, restricted to visible
 * (non-deleted, non-hidden) conversations. Returns a map keyed by codetype id;
 * codetypes with no codes are simply absent. Pages through the rows in
 * PostgREST-max_rows-sized batches — a single unpaged select is silently
 * truncated at max_rows (1000) and would undercount large interviews.
 */
export async function getConversationCodeCounts(
  supabase: SupabaseClient,
  codetypeIds: number[]
): Promise<Map<number, number>> {
  if (codetypeIds.length === 0) return new Map();

  const PAGE = 1000;
  const seen = new Map<number, Set<number>>();
  for (let offset = 0; ; offset += PAGE) {
    const { data } = await supabase
      .from('conversation_codes')
      .select(
        'codetype_id, conversation_id, conversations!inner(deleted, hidden)'
      )
      .in('codetype_id', codetypeIds)
      .eq('conversations.deleted', false)
      .eq('conversations.hidden', false)
      .order('id', { ascending: true })
      .range(offset, offset + PAGE - 1)
      .throwOnError();

    for (const row of data ?? []) {
      const set = seen.get(row.codetype_id) ?? new Set<number>();
      set.add(row.conversation_id);
      seen.set(row.codetype_id, set);
    }
    if (!data || data.length < PAGE) break;
  }

  const counts = new Map<number, number>();
  seen.forEach((convs, id) => counts.set(id, convs.size));
  return counts;
}

/** First codetype id for the interview where `column` equals `value`, else null. */
async function findCodetypeId(
  supabase: SupabaseClient,
  interviewId: number,
  column: 'name' | 'description',
  value: string
): Promise<number | null> {
  const { data } = await supabase
    .from('interview_codetypes')
    .select('id')
    .eq('interview_id', interviewId)
    .eq(column, value)
    .limit(1)
    .maybeSingle()
    .throwOnError();
  return data?.id ?? null;
}
